/**
 * Helpers voor het lezen/schrijven van een Roadmap-matrix-diagram-configuratie
 * (roadmap-tijdreizen.md §6.4).
 *
 * De config wordt geserialiseerd onder
 * `Diagram.viewState[ROADMAP_MATRIX_CONFIG_KEY]` — geen aparte D1-kolom, geen
 * migratie. Zelfde stash-in-viewState-patroon als `heatMapConfig.ts`; de
 * sentinel-entry hergebruikt `ViewElementState.style` als prop-bag zodat
 * round-tripping door bestaande JSON-paths intact blijft (de renderer negeert
 * deze entry omdat de key niet matcht met een concept/relation-id).
 */
import {
  type BilingualText,
  ROADMAP_MATRIX_CONFIG_KEY,
  type RoadmapMatrixConfig,
  type ViewState,
} from "@ae/domain";

export function readRoadmapMatrixConfig(
  viewState: ViewState | undefined | null,
): RoadmapMatrixConfig {
  const entry = viewState?.[ROADMAP_MATRIX_CONFIG_KEY];
  const blob = entry?.style as Record<string, unknown> | undefined;
  if (!blob) return {};
  const out: RoadmapMatrixConfig = {};
  if (typeof blob.programId === "string") out.programId = blob.programId;
  if (typeof blob.conceptFilter === "string") out.conceptFilter = blob.conceptFilter;
  if (Array.isArray(blob.rowConceptIds)) {
    out.rowConceptIds = blob.rowConceptIds.filter((x): x is string => typeof x === "string");
  }
  if (blob.rowStates && typeof blob.rowStates === "object") {
    const src = blob.rowStates as Record<string, unknown>;
    const states: Record<string, Record<string, BilingualText>> = {};
    for (const [conceptId, perPlateau] of Object.entries(src)) {
      if (!perPlateau || typeof perPlateau !== "object") continue;
      const inner: Record<string, BilingualText> = {};
      for (const [plateauId, v] of Object.entries(perPlateau as Record<string, unknown>)) {
        // Legacy: platte string → toon in beide talen (geen verlies; een latere
        // edit triggert auto-translate). Nieuw: BilingualText-object.
        if (typeof v === "string" && v) {
          inner[plateauId] = { nl: v, en: v };
        } else if (v && typeof v === "object") {
          const bt: BilingualText = {};
          for (const [lang, text] of Object.entries(v as Record<string, unknown>)) {
            if (typeof text === "string" && text) bt[lang] = text;
          }
          if (Object.keys(bt).length > 0) inner[plateauId] = bt;
        }
      }
      if (Object.keys(inner).length > 0) states[conceptId] = inner;
    }
    if (Object.keys(states).length > 0) out.rowStates = states;
  }
  return out;
}

/**
 * Bouwt een nieuwe viewState met de config bijgewerkt. Mutates niet.
 */
export function writeRoadmapMatrixConfig(
  viewState: ViewState,
  config: RoadmapMatrixConfig,
): ViewState {
  const blob: Record<string, unknown> = {};
  if (config.programId) blob.programId = config.programId;
  if (config.conceptFilter) blob.conceptFilter = config.conceptFilter;
  if (config.rowConceptIds && config.rowConceptIds.length > 0) {
    blob.rowConceptIds = config.rowConceptIds;
  }
  if (config.rowStates && Object.keys(config.rowStates).length > 0) {
    blob.rowStates = config.rowStates;
  }
  return {
    ...viewState,
    [ROADMAP_MATRIX_CONFIG_KEY]: {
      type: "object",
      ref: ROADMAP_MATRIX_CONFIG_KEY,
      style: blob,
    },
  };
}

/** Een matrix is geconfigureerd zodra Program + concept-type-filter gekozen zijn. */
export function isRoadmapMatrixConfigured(config: RoadmapMatrixConfig): boolean {
  return Boolean(config.programId && config.conceptFilter);
}
